// src/views/tickets/hooks/useTicketDetail.js
import { useSelector, useDispatch } from 'react-redux'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import React from 'react'
import { Notification, toast } from 'components/ui'
import { useTranslation } from 'react-i18next'
import usePermissions from 'utils/hooks/usePermissions'
import detectOrigin from '../utils/detectOrigin'
import {
  apiPutTicket,
  apiTicketFavorite,
  apiChangeSubscriptionTicket,
} from 'services/TicketsService'
import { statusResponse } from 'constants/apiResponse.constant'
import {
  deleteDuplicateData,
  deleteDuplicateDataToFinal,
  showMessageError,
  setUserObject,
} from 'utils/storeHube/helpers'
import { STORE_HUB } from 'components/ui/utils/constant'
import {
  getUsersAllowed,
  setSelectedTicket,
  updateTicketList,
  markTicketInProgressIfNeeded,
} from '../store/dataSlice'
import {
  toggleChangeOperator,
  toggleDeleteConfirmation,
  setSelectedTicketSlice,
  toggleLinkTicket,
  toggleTicketDialog,
  setDialogView,
} from '../store/stateSlice'
import { getAllUsers, setParentIdForTicket } from '../store/globalSlice'

const CLOSED = 4
const ASSIGNED = 2

function safeCheckPermission(perms, action) {
  try {
    return perms.checkPermission?.(action) ?? false
  } catch (_) {
    return false
  }
}

function useInboxSource() {
  const dispatch = useDispatch()
  const currentUser = useSelector((s) => s.auth.user)
  const { ticketStatus, allUsers } = useSelector((s) => s.ticketList.global)
  const {
    selectedTicket,
    selectedTicketId,
    loadingSelectedTicket,
    loadingUsersAllowed,
    responsableMap,
    ticketList,
    tableData,
  } = useSelector((s) => s.ticketList.data)

  const actions = useMemo(
    () => ({
      changeOperator: () => dispatch(toggleChangeOperator(true)),
      markInProgress: (args) => dispatch(markTicketInProgressIfNeeded(args)),
      setSelected: (t) => dispatch(setSelectedTicket(t)),
      updateList: (l) => dispatch(updateTicketList(l)),
    }),
    [dispatch]
  )

  return {
    dispatch,
    ticket: selectedTicket,
    selectedTicketId,
    ticketList: ticketList || [],
    currentUser,
    ticketStatus,
    allUsers,
    responsableMap: responsableMap || {},
    loading: loadingSelectedTicket,
    loadingUsersAllowed,
    // Qué lista está mirando la persona. El detalle es presentacional y no toca
    // Redux, así que la vista activa tiene que llegarle por acá.
    fromMentionsView: tableData?.filterSelected?.name === 'mentioned',
    actions,
  }
}

export function useTicketDetail({ source }) {
  const src = useInboxSource()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const perms = usePermissions()

  const operator = src.ticket?.props?.operator
  const owner = src.ticket?.props?.owner
  const store = src.ticket?.props?.store

  const isAssignedToMe = useMemo(
    () => !!src.currentUser?.id && src.currentUser.id === operator?.id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src.currentUser?.id, operator?.id]
  )

  const canViewQuestionAndResponse = useMemo(
    () =>
      src.currentUser?.id === owner?.id ||
      src.currentUser?.id === operator?.id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src.currentUser?.id, owner?.id, operator?.id]
  )

  const ticketOrigin = useMemo(() => detectOrigin(src.ticket), [src.ticket])
  // Una categoria de tipo 'notification' SIEMPRE es informativa (CTA "Marcar
  // como leido"), sin importar quien genero el ticket. detectOrigin solo
  // reconoce el caso D4 (notificacion de bot sin cuestionario), asi que una
  // notificacion creada por un humano perdia el boton (bug STORE-172/T12). Las
  // categorias 'action' tienen cuestionario y NO entran aca: usan el flujo de
  // responder/cerrar, no "marcar como leido".
  const notificationCategoryType =
    src.ticket?.props?.ticket_category_secondary?.type ??
    src.ticket?.ticket_category_secondary?.type
  const isNotificationCategory = notificationCategoryType === 'notification'
  const isInformational =
    ticketOrigin?.key === 'D4' || isNotificationCategory

  // Tri-state simplificado: el componente refinara con el fetch async
  // de questionGroup. En este task solo necesitamos exponer el insumo.
  const questionnaireState = canViewQuestionAndResponse ? 'ready' : 'hidden'

  const permissions = useMemo(
    () => ({
      canUpdate: safeCheckPermission(perms, 'tickets.can_update'),
      canChangeStatus: safeCheckPermission(perms, 'tickets.can_update'), // cambiar estado usa el mismo permiso que actualizar
      canClose: safeCheckPermission(perms, 'tickets.can_close'),
      canReopen: safeCheckPermission(perms, 'tickets.can_reopen'),
      canCancel: safeCheckPermission(perms, 'tickets.can_cancel'),
      canLink: safeCheckPermission(perms, 'tickets.can_link'),
      canViewTimeline: safeCheckPermission(perms, 'tickets.can_view_timeline'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perms]
  )

  // ---------- efectos de carga de datos ----------

  // Una sola vez por montaje del contenedor: carga el catalogo de usuarios.
  useEffect(() => {
    src.dispatch(getAllUsers())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cada vez que cambia el ticket seleccionado: recarga los usuarios permitidos
  // para la tienda del ticket (operadores disponibles, mapa de responsables).
  useEffect(() => {
    if (!src.selectedTicketId || !store?.id) return
    src.dispatch(getUsersAllowed(store.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src.selectedTicketId])

  // ---------- callbacks wired ----------

  // Cambia el estado del ticket via API y actualiza Redux.
  // Devuelve true si se aplico el cambio, false si fallo.
  const onChangeStatus = useMemo(
    () => async (status_id, ticketId) => {
      try {
        const { status, data } = await apiPutTicket(ticketId, {
          ticket_status_id: status_id,
        })
        if (status === statusResponse.SUCCESS) {
          const newTicketList =
            status_id !== CLOSED
              ? deleteDuplicateData(src.ticketList, data.data)
              : deleteDuplicateDataToFinal(src.ticketList, data.data)
          src.dispatch(updateTicketList(newTicketList))
          src.dispatch(setSelectedTicket(data.data))
          toast.push(
            <Notification title={t('updated')} type="success" duration={2500}>
              {t('ticket_successUpdate')}
            </Notification>
          )
          return true
        }
        return false
      } catch (error) {
        showMessageError(error?.response?.data?.message || error?.message)
        return false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src.dispatch, src.ticketList, t]
  )

  // onMarkAsRead: alias de onChangeStatus con CLOSED pre-fijado.
  // TicketDetail lo usa internamente en TicketPrimaryActions a traves
  // de su wrapper local setNewTicketStatus (que ya llama a onChangeStatus).
  const onMarkAsRead = useMemo(
    () => async () => {
      if (!src.ticket?.id) return
      return onChangeStatus(CLOSED, src.ticket.id)
    },
    [onChangeStatus, src.ticket?.id]
  )

  const onToggleFavorite = useMemo(
    () => async () => {
      if (!src.ticket) return
      const ticketId = src.ticket.id
      const is_favorite = src.ticket.is_favorite
      const optimistic = { ...src.ticket, is_favorite: !is_favorite }
      try {
        const { status } = await apiTicketFavorite(ticketId)
        if (status === statusResponse.SUCCESS) {
          const newTicketList = deleteDuplicateData(src.ticketList, optimistic)
          src.dispatch(updateTicketList(newTicketList))
          src.dispatch(setSelectedTicket(optimistic))
        }
      } catch (error) {
        console.log(error)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src.dispatch, src.ticketList, src.ticket]
  )

  const onSetSubscriber = useMemo(
    () => async () => {
      if (!src.ticket || !src.currentUser) return
      const ticket = src.ticket
      const user = src.currentUser
      const ticketId = ticket.id
      const subscriptions = ticket.props?.ticket_subscriptions || []
      const haveSubscription = !!subscriptions.find((s) => s.email === user.email)
      try {
        const res = await apiChangeSubscriptionTicket(ticketId)
        if (res.status === statusResponse.SUCCESS) {
          const userSub = setUserObject(user)
          const newSubscriptions = haveSubscription
            ? subscriptions.filter((s) => s.email !== userSub.email)
            : [...subscriptions, userSub]
          const updated = {
            ...ticket,
            props: { ...ticket.props, ticket_subscriptions: newSubscriptions },
          }
          src.dispatch(setSelectedTicket(updated))
          toast.push(
            <Notification
              title={haveSubscription ? t('unsuscribe') : t('suscribe')}
              type="success"
              duration={2500}
            >
              {haveSubscription ? t('ticket_unsuscribe') : t('ticket_suscribe')}
            </Notification>
          )
        }
      } catch (error) {
        console.log(error)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src.dispatch, src.ticket, src.currentUser, t]
  )

  // onQuestionnaireResolution: orquesta la accion al responder el cuestionario.
  // 'reassign' -> asigna al primer candidato del rol; fallback: abre modal manual.
  // 'resolve'  -> cierra el hallazgo (status CLOSED, skipQuestionCheck = true
  //               — pero el skip lo maneja TicketDetail en su wrapper local).
  const onQuestionnaireResolution = useMemo(
    () => async (action, reassignToRole) => {
      if (action === 'reassign') {
        const candidates = reassignToRole ? src.responsableMap?.[reassignToRole] : null
        const firstCandidate =
          Array.isArray(candidates) && candidates.length > 0 ? candidates[0] : null
        const targetUserId =
          typeof firstCandidate === 'object' ? firstCandidate?.id : firstCandidate
        if (targetUserId) {
          try {
            const { status, data } = await apiPutTicket(src.ticket?.id, {
              operator_id: targetUserId,
              ticket_status_id: ASSIGNED,
            })
            if (status === statusResponse.SUCCESS) {
              const newTicketList = deleteDuplicateData(src.ticketList, data.data)
              src.dispatch(updateTicketList(newTicketList))
              src.dispatch(setSelectedTicket(data.data))
              return true
            }
            return false
          } catch (e) {
            return false
          }
        }
        // Fallback: rol no configurado o sin usuarios para esta tienda.
        src.dispatch(toggleChangeOperator(true))
        return true
      }
      if (action === 'resolve') {
        return onChangeStatus(CLOSED, src.ticket?.id)
      }
      return true
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src.dispatch, src.ticket, src.ticketList, src.responsableMap, onChangeStatus]
  )

  const onOpenParent = useMemo(
    () => () => {
      const parent_id = src.ticket?.parent_id
      if (!parent_id) return
      navigate(`/tickets/inbox?ticket=${parent_id}`)
    },
    [src.ticket?.parent_id, navigate]
  )

  // Navegacion a cualquier vinculada desde la card del aside. Va en la misma
  // pestana, igual que onOpenParent: antes la lista de derivadas abria en una
  // pestana nueva y recargaba la app entera.
  const onOpenTicket = useMemo(
    () => (id) => {
      if (!id) return
      navigate(`/tickets/inbox?ticket=${id}`)
    },
    [navigate]
  )

  const onAddLinkedTicket = useMemo(
    () => () => {
      if (!src.ticket?.id) return
      src.dispatch(setParentIdForTicket(src.ticket.id))
      src.dispatch(setDialogView(STORE_HUB.ADD_TICKET))
      src.dispatch(toggleTicketDialog(true))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src.dispatch, src.ticket?.id]
  )

  const onLinkExisting = useMemo(
    () => () => {
      src.dispatch(toggleLinkTicket(true))
    },
    [src.dispatch]
  )

  // Guard de permiso restaurado del handler original handeDeleteTicket:
  // solo el operador asignado puede abrir el modal de borrar.
  const onDelete = useMemo(
    () => () => {
      const ticketOperator = src.ticket?.props?.operator
      if (ticketOperator?.id === src.currentUser?.id) {
        src.dispatch(setSelectedTicketSlice(src.ticket))
        src.dispatch(toggleDeleteConfirmation(true))
      } else {
        toast.push(
          <Notification title={t('change_status')} type="danger" duration={2000}>
            <div>
              {`${t('ticket_no_permission_cancel')} `}
              <span className="lowercase">{t('ticket_alias')}</span>
            </div>
          </Notification>
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src.dispatch, src.ticket, src.currentUser, t]
  )

  return {
    ticket: src.ticket,
    currentUser: src.currentUser,
    ticketStatus: src.ticketStatus,
    allUsers: src.allUsers,
    responsableMap: src.responsableMap,
    loading: src.loading,
    loadingUsersAllowed: src.loadingUsersAllowed,
    isAssignedToMe,
    canViewQuestionAndResponse,
    questionnaireState,
    ticketOrigin,
    isInformational,
    fromMentionsView: src.fromMentionsView,
    permissions,
    onChangeStatus,
    onChangeOperator: src.actions.changeOperator,
    onMarkAsRead,
    onToggleFavorite,
    onQuestionnaireResolution,
    onMarkInProgress: src.actions.markInProgress,
    onOpenParent,
    onOpenTicket,
    onDelete,
    onAddLinkedTicket,
    onSetSubscriber,
    onLinkExisting,
  }
}
